/**
 * screens.ts — หน้าจอรายการรายได้/ค่าใช้จ่าย, Payroll, ทรัพย์สิน, ภาษี, การปันส่วน (T090/T091/T094/T095/T098)
 * ทุกหน้าใช้ active tenant; โหมด demo (ไม่มี API) แสดงข้อความให้เข้าสู่ระบบ
 */
import type { Page } from './router.js';
import { getTenant } from './state.js';
import { fmtDate } from './format.js';
import { pnlCategoryLabel, PNL_CATEGORY_OPTIONS } from '@rrp/shared';
import * as api from './api.js';
import {
  esc,
  money,
  pageShell,
  dataTable,
  pill,
  val,
  opt,
  checked,
  bindSubmit,
  errorBox,
  pagerHtml,
  bindPager,
  showDrawer,
  drawerHtml,
  fieldLabel,
  CLASSIFICATION_LABELS,
  promptDialog,
  type TableRow,
} from './ui.js';

type Row = Record<string, unknown>;

/** refresh ของตารางในแต่ละหน้า (ให้ส่วนขยายเรียกได้โดยไม่ต้อง reload ทั้งหน้า) */
const refreshers = new WeakMap<HTMLElement, () => Promise<void>>();
const refreshList = async (root: HTMLElement): Promise<void> => {
  await refreshers.get(root)?.();
};

/** helper: หน้าที่มีฟอร์มกรอก + ตารางรายการ (โหลด async หลัง mount) */
function crudPage(opts: {
  title: string;
  formHtml: string;
  onSubmit: (form: HTMLFormElement) => Promise<void>;
  load: () => Promise<string>;
  extra?: string;
}): Page {
  return {
    title: opts.title,
    render: () =>
      pageShell(
        opts.title,
        `<section class="card"><form id="crud-form" class="grid-form">${opts.formHtml}
          <div class="form-actions"><button type="submit">บันทึก</button><span id="msg" class="muted"></span></div>
        </form></section>
        <section class="card"><div id="list">กำลังโหลด…</div></section>${opts.extra ?? ''}${drawerHtml}`,
      ),
    mount(root) {
      if (!getTenant()) return;
      const listEl = root.querySelector<HTMLElement>('#list')!;
      const refresh = async (): Promise<void> => {
        try {
          listEl.innerHTML = await opts.load();
        } catch (e) {
          listEl.innerHTML = errorBox(e);
        }
      };
      refreshers.set(root, refresh);
      const form = root.querySelector<HTMLFormElement>('#crud-form');
      const msg = root.querySelector<HTMLElement>('#msg')!;
      if (form) bindSubmit(form, msg, opts.onSubmit, refresh);
      void refresh();
    },
  };
}

// ---------------------------------------------------------------------------
// T090 — รายการรายได้/ค่าใช้จ่าย (ค้นหา กรอง แก้ไข ประวัติ ค้างจ่าย review)
// ---------------------------------------------------------------------------
const CLASS_OPTIONS = Object.entries(CLASSIFICATION_LABELS)
  .map(([v, l]) => `<option value="${v}">${l}</option>`)
  .join('');


function entryRows(items: Row[]): TableRow[] {
  return items.map((r) => {
    const outstanding = Number(r['outstanding_amount'] ?? 0);
    const cls = String(r['classification'] ?? '');
    return {
      cells: [
        esc(r['event_date'] ? fmtDate(String(r['event_date'])) : '—'),
        esc(CLASSIFICATION_LABELS[cls] ?? cls),
        esc(r['counterparty_name'] ?? '—'),
        esc(r['pnl_category'] ? pnlCategoryLabel(String(r['pnl_category'])) : '—'),
        `<span class="num">${money(r['total_amount'])}</span>`,
        `<span class="num">${money(r['paid_amount'])}</span>`,
        outstanding > 0
          ? `<span class="num warn-text">${money(outstanding)}</span>`
          : `<span class="num">${money(0)}</span>`,
        r['review_status'] === 'needs_review'
          ? pill('ต้องตรวจสอบ', 'warn')
          : r['pnl_impact'] === false
            ? pill('ไม่กระทบ P&L')
            : pill('ปกติ', 'ok'),
        `<button class="mini" data-edit="${esc(r['id'])}">แก้ไข</button>
         <button class="mini" data-hist="${esc(r['id'])}">ประวัติ</button>
         <button class="mini" data-rev="${esc(r['id'])}">กลับรายการ</button>`,
      ],
    };
  });
}

const EDITABLE_ENTRY_FIELDS = [
  'eventDate',
  'accountingDate',
  'paymentDate',
  'counterpartyName',
  'pnlCategory',
  'classification',
  'amountBeforeVat',
  'vatAmount',
  'whtAmount',
  'totalAmount',
  'paidAmount',
] as const;

function entryEditForm(r: Row): string {
  const v = (k: string): string => esc(r[k] ?? '');
  /** input ที่จำค่าเดิมไว้ (data-original) เพื่อส่งเฉพาะฟิลด์ที่เปลี่ยน */
  const field = (name: string, label: string, col: string, type = 'text'): string =>
    `<label>${label}<input name="${name}" type="${type}" value="${v(col)}" data-original="${v(col)}"></label>`;
  return `<form id="edit-form" class="grid-form">
    ${field('eventDate', 'วันที่เกิดรายการ', 'event_date', 'date')}
    ${field('accountingDate', 'วันที่ลงบัญชี', 'accounting_date', 'date')}
    ${field('paymentDate', 'วันที่จ่ายจริง', 'payment_date', 'date')}
    ${field('counterpartyName', 'คู่ค้า', 'counterparty_name')}
    <label>หมวด P&L<select name="pnlCategory" data-original="${v('pnl_category')}">
      <option value="">— ไม่ระบุ —</option>
      ${PNL_CATEGORY_OPTIONS.map(
        (o) =>
          `<option value="${o.value}"${r['pnl_category'] === o.value ? ' selected' : ''}>${o.label}</option>`,
      ).join('')}</select></label>
    <label>ประเภทรายการ<select name="classification" data-original="${v('classification')}">${Object.entries(
      CLASSIFICATION_LABELS,
    )
      .map(
        ([k, l]) =>
          `<option value="${k}"${r['classification'] === k ? ' selected' : ''}>${l}</option>`,
      )
      .join('')}</select></label>
    ${field('amountBeforeVat', 'ยอดก่อน VAT', 'amount_before_vat')}
    ${field('vatAmount', 'VAT', 'vat_amount')}
    ${field('whtAmount', 'หัก ณ ที่จ่าย', 'wht_amount')}
    ${field('totalAmount', 'ยอดรวม', 'total_amount')}
    ${field('paidAmount', 'จ่ายจริง', 'paid_amount')}
    <label>เหตุผลการแก้ไข (บังคับเมื่องวดปิด)<input name="reason" placeholder="เช่น แก้ยอดตามใบกำกับ"></label>
    <div class="form-actions"><button type="submit">บันทึกการแก้ไข</button><span id="edit-msg" class="muted"></span></div>
  </form>
  <p class="foot-note">งวดที่ปิดแล้วแก้ทับไม่ได้ — ต้องเปลี่ยนประเภทเป็น “รายการปรับปรุง” และระบุเหตุผล (ระบบจะแจ้งถ้างวดปิด)</p>`;
}

function auditHistoryHtml(items: api.AuditRow[]): string {
  if (items.length === 0) return '<p class="muted">ยังไม่มีประวัติการแก้ไข</p>';
  return items
    .map((a) => {
      const changes = diffRows(a.oldData, a.newData);
      return `<div class="hist-item">
        <div class="hist-head"><strong>${esc(actionLabel(a.action))}</strong>
          <span class="muted">${esc(a.createdAt.replace('T', ' ').slice(0, 19))}</span>
          ${a.reason ? `<span class="muted">· เหตุผล: ${esc(a.reason)}</span>` : ''}
          <span class="muted">· request ${esc(a.requestId ?? '—')}</span></div>
        ${changes}
      </div>`;
    })
    .join('');
}

function actionLabel(action: string): string {
  return (
    {
      create: 'สร้างรายการ',
      update: 'แก้ไข',
      override: 'แก้ไขทับ (override)',
      revision: 'สร้างเวอร์ชันใหม่',
      reversal: 'กลับรายการ',
      delete_denied: 'ปฏิเสธการลบ',
    }[action] ?? action
  );
}

function diffRows(oldData: unknown, newData: unknown): string {
  const o = (oldData ?? {}) as Record<string, unknown>;
  const n = (newData ?? {}) as Record<string, unknown>;
  const rows = Object.keys(n)
    .filter((k) => JSON.stringify(o[k] ?? null) !== JSON.stringify(n[k] ?? null))
    .filter((k) => !['account_id', 'business_id', 'created_by'].includes(k))
    .map(
      (k) =>
        `<tr><td>${esc(fieldLabel(k))}</td><td class="muted">${esc(fmtVal(o[k]))}</td><td>→ ${esc(fmtVal(n[k]))}</td></tr>`,
    );
  if (rows.length === 0) return '<p class="muted">ไม่มีฟิลด์ที่เปลี่ยน</p>';
  return `<table class="data"><tbody>${rows.join('')}</tbody></table>`;
}

function fmtVal(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * แผงเอกสารแนบทั่วไปใน drawer (T104/T094) — ใช้ซ้ำได้กับรายการค่าใช้จ่ายและทรัพย์สิน
 * ทำงานเมื่อเปิด R2 แล้ว; ถ้ายังไม่เปิด endpoint คืน 404 → แสดงข้อความแทน (ไม่ทำ drawer พัง)
 */
function mountAttachmentsPanel(
  host: HTMLElement,
  opts: {
    title: string;
    downloadTarget: 'entry' | 'document';
    list: () => Promise<{ items: api.AttachmentRow[] }>;
    upload: (file: File) => Promise<unknown>;
  },
): void {
  const body = host.querySelector<HTMLElement>('.drawer-body') ?? host;
  const panel = document.createElement('section');
  panel.className = 'card';
  panel.style.marginTop = '12px';
  panel.innerHTML = `<h4>${esc(opts.title)}</h4>
    <div class="att-list muted">กำลังโหลด…</div>
    <form class="att-form grid-form">
      <label>แนบไฟล์ (PDF/รูป/CSV/Excel สูงสุด 20MB)
        <input type="file" name="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xls,.xlsx" required></label>
      <div class="form-actions"><button type="submit">อัปโหลด</button><span class="att-msg muted"></span></div>
    </form>`;
  body.appendChild(panel);
  const listEl = panel.querySelector<HTMLElement>('.att-list')!;
  const msg = panel.querySelector<HTMLElement>('.att-msg')!;
  const form = panel.querySelector<HTMLFormElement>('.att-form')!;

  const load = async (): Promise<void> => {
    try {
      const { items } = await opts.list();
      listEl.innerHTML = items.length
        ? `<table class="data"><tbody>${items
            .map(
              (a) =>
                `<tr><td>${esc(a.filename ?? a.id)}</td><td class="muted">${
                  a.sizeBytes ? `${Math.ceil(a.sizeBytes / 1024)} KB` : ''
                }</td><td><button class="mini" type="button" data-dl="${esc(a.id)}">ดาวน์โหลด</button></td></tr>`,
            )
            .join('')}</tbody></table>`
        : '<p class="muted">ยังไม่มีเอกสารแนบ</p>';
      listEl.querySelectorAll<HTMLButtonElement>('[data-dl]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          try {
            const signed = await api.getAttachmentUrl(btn.dataset['dl']!, opts.downloadTarget);
            window.open(api.downloadHref(signed.url), '_blank', 'noopener');
          } catch (e) {
            msg.textContent = (e as Error).message;
          }
        });
      });
    } catch (e) {
      // R2 ยังไม่เปิด (404) หรือ error อื่น — แจ้งแบบไม่ทำ drawer พัง
      listEl.innerHTML = `<p class="muted">${esc((e as Error).message)}</p>`;
    }
  };

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const input = form.elements.namedItem('file') as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    msg.textContent = 'กำลังอัปโหลด…';
    try {
      await opts.upload(file);
      msg.textContent = 'อัปโหลดสำเร็จ';
      form.reset();
      await load();
    } catch (e) {
      msg.textContent = (e as Error).message;
    }
  });

  void load();
}

function mountEntryAttachments(drawer: HTMLElement, entryId: string): void {
  mountAttachmentsPanel(drawer, {
    title: 'เอกสารแนบ',
    downloadTarget: 'entry',
    list: () => api.listEntryAttachments(entryId),
    upload: (file) => api.uploadAttachment({ target: 'entry', entryId, file }),
  });
}

export const entriesPage: Page = {
  title: 'รายการรายได้/ค่าใช้จ่าย',
  render: () =>
    pageShell(
      'รายการรายได้/ค่าใช้จ่าย',
      `<section class="card">
        <form id="filter-form" class="grid-form" role="search">
          <label>ตั้งแต่วันที่<input name="from" type="date"></label>
          <label>ถึงวันที่<input name="to" type="date"></label>
          <label>ประเภทรายการ<select name="classification"><option value="">ทั้งหมด</option>${CLASS_OPTIONS}</select></label>
          <label>สถานะตรวจสอบ<select name="reviewStatus"><option value="">ทั้งหมด</option><option value="needs_review">ต้องตรวจสอบ</option><option value="ok">ปกติ</option></select></label>
          <label>ค้นหา (คู่ค้า/เลขที่เอกสาร/แคมเปญ)<input name="search" type="search" placeholder="เช่น ชื่อผู้ขาย"></label>
          <div class="form-actions"><button type="submit">กรอง</button><button type="button" id="filter-reset" class="mini">ล้างตัวกรอง</button><span id="filter-msg" class="muted"></span></div>
        </form>
      </section>
      <section class="card"><div id="list">กำลังโหลด…</div></section>
      ${drawerHtml}`,
      `<a class="btn-link" href="#entry">+ กรอกรายการใหม่</a>`,
    ),
  mount(root) {
    if (!getTenant()) return;
    const listEl = root.querySelector<HTMLElement>('#list')!;
    const filterForm = root.querySelector<HTMLFormElement>('#filter-form')!;
    const state = { limit: 25, offset: 0 };

    const currentFilter = (): api.EntryFilter => ({
      from: opt(filterForm, 'from') ?? '',
      to: opt(filterForm, 'to') ?? '',
      classification: opt(filterForm, 'classification') ?? '',
      reviewStatus: opt(filterForm, 'reviewStatus') ?? '',
      search: opt(filterForm, 'search') ?? '',
      limit: state.limit,
      offset: state.offset,
    });

    const openEdit = async (id: string): Promise<void> => {
      const entry = await api.getEntry(id);
      const drawer = showDrawer(root, 'แก้ไขรายการ', entryEditForm(entry));
      const form = drawer.querySelector<HTMLFormElement>('#edit-form')!;
      const msg = drawer.querySelector<HTMLElement>('#edit-msg')!;
      mountEntryAttachments(drawer, id);
      bindSubmit(
        form,
        msg,
        async (f) => {
          // ส่งเฉพาะฟิลด์ที่ผู้ใช้เปลี่ยนจริง → audit log สะอาดและไม่เขียนทับค่าที่ไม่ได้แก้
          const patch: Row = {};
          for (const key of EDITABLE_ENTRY_FIELDS) {
            const el = f.elements.namedItem(key) as HTMLInputElement | HTMLSelectElement | null;
            if (!el) continue;
            const current = el.value.trim();
            const original = (el.dataset['original'] ?? '').trim();
            if (current !== original && current !== '') patch[key] = current;
          }
          const reason = opt(f, 'reason');
          if (reason) patch['reason'] = reason;
          if (Object.keys(patch).length === 0) throw new Error('ยังไม่มีการเปลี่ยนแปลงให้บันทึก');
          await api.updateEntry(id, patch);
        },
        refresh,
        { reset: false },
      );
    };

    const openHistory = async (id: string): Promise<void> => {
      showDrawer(root, 'ประวัติการแก้ไข', '<p class="muted">กำลังโหลด…</p>');
      const { items } = await api.listAudit({
        entityTable: 'financial_entries',
        entityId: id,
        limit: 50,
      });
      showDrawer(root, `ประวัติการแก้ไข (${items.length} เหตุการณ์)`, auditHistoryHtml(items));
    };

    async function refresh(): Promise<void> {
      listEl.innerHTML = 'กำลังโหลด…';
      try {
        const { items, total } = await api.listEntries(currentFilter());
        const outstanding = items.reduce((s, r) => s + Number(r['outstanding_amount'] ?? 0), 0);
        listEl.innerHTML =
          `<p class="muted">พบ ${total ?? items.length} รายการ · ค้างจ่ายในหน้านี้ ${money(outstanding)}</p>` +
          dataTable(
            ['วันที่', 'ประเภท', 'คู่ค้า', 'หมวด P&L', 'ยอดรวม', 'จ่ายจริง', 'ค้างจ่าย', 'สถานะ', ''],
            entryRows(items),
            'ไม่พบรายการตามตัวกรอง',
          ) +
          pagerHtml(total ?? items.length, state.limit, state.offset);
        bindPager(listEl, state, refresh);
        listEl.querySelectorAll<HTMLButtonElement>('button[data-edit]').forEach((b) =>
          b.addEventListener('click', () => {
            void openEdit(b.dataset['edit']!).catch((e: unknown) =>
              showDrawer(root, 'แก้ไขรายการ', errorBox(e)),
            );
          }),
        );
        listEl.querySelectorAll<HTMLButtonElement>('button[data-hist]').forEach((b) =>
          b.addEventListener('click', () => {
            void openHistory(b.dataset['hist']!).catch((e: unknown) =>
              showDrawer(root, 'ประวัติการแก้ไข', errorBox(e)),
            );
          }),
        );
        listEl.querySelectorAll<HTMLButtonElement>('button[data-rev]').forEach((b) =>
          b.addEventListener('click', async () => {
            const reason = await promptDialog({
              title: 'กลับรายการ (Reversal)',
              label: 'เหตุผลการกลับรายการ',
              placeholder: 'เช่น บันทึกผิด/ยกเลิกรายการ',
              confirmText: 'กลับรายการ',
            });
            if (!reason) return;
            try {
              await api.reverseEntry(b.dataset['rev']!, reason);
              await refresh();
            } catch (e) {
              alert(e instanceof Error ? e.message : 'กลับรายการไม่สำเร็จ');
            }
          }),
        );
      } catch (e) {
        listEl.innerHTML = errorBox(e);
      }
    }

    filterForm.addEventListener('submit', (e) => {
      e.preventDefault();
      state.offset = 0;
      void refresh();
    });
    root.querySelector('#filter-reset')?.addEventListener('click', () => {
      filterForm.reset();
      state.offset = 0;
      void refresh();
    });
    void refresh();
  },
};

// ---------------------------------------------------------------------------
// T091 — Payroll (กรอก + อัปโหลด CSV สรุปงวด)
// ---------------------------------------------------------------------------
const PAYROLL_GROSS = [
  ['salary', 'เงินเดือน'],
  ['wage', 'ค่าจ้าง'],
  ['ot', 'OT'],
  ['bonus', 'โบนัส'],
  ['commission', 'คอมมิชชัน'],
  ['allowance', 'เบี้ยเลี้ยง'],
  ['welfare', 'สวัสดิการ'],
] as const;
const PAYROLL_DEDUCTIONS = [
  ['employerSso', 'ประกันสังคมนายจ้าง (ค่าใช้จ่าย)'],
  ['employerWcf', 'กองทุนเงินทดแทน (ค่าใช้จ่าย)'],
  ['employeeSso', 'ประกันสังคมพนักงาน (หัก)'],
  ['employeeTax', 'ภาษีพนักงาน (หัก)'],
  ['employeeLoanGsl', 'กยศ. (หัก)'],
  ['otherDeductions', 'หักอื่น ๆ'],
] as const;
const PAYROLL_FIELDS = [...PAYROLL_GROSS, ...PAYROLL_DEDUCTIONS];

/** parse CSV สรุปเงินเดือน: header = periodYear,periodMonth,costCenter,salary,... */
export function parsePayrollCsv(text: string): Row[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) throw new Error('ไฟล์ต้องมีบรรทัดหัวตารางและข้อมูลอย่างน้อย 1 แถว');
  const header = lines[0]!.split(',').map((h) => h.trim());
  const known = new Set(['periodYear', 'periodMonth', 'costCenter', ...PAYROLL_FIELDS.map(([k]) => k)]);
  const unknown = header.filter((h) => !known.has(h));
  if (unknown.length > 0) throw new Error(`คอลัมน์ที่ไม่รู้จัก: ${unknown.join(', ')}`);
  if (!header.includes('periodYear') || !header.includes('periodMonth')) {
    throw new Error('ต้องมีคอลัมน์ periodYear และ periodMonth');
  }
  return lines.slice(1).map((line, idx) => {
    const cells = line.split(',').map((c) => c.trim());
    const row: Row = {};
    header.forEach((h, i) => {
      const raw = cells[i] ?? '';
      if (h === 'periodYear' || h === 'periodMonth') {
        const n = Number(raw);
        if (!Number.isFinite(n)) throw new Error(`แถวที่ ${idx + 1}: ${h} ต้องเป็นตัวเลข`);
        row[h] = n;
      } else if (h === 'costCenter') {
        if (raw) row[h] = raw;
      } else {
        row[h] = raw || '0';
      }
    });
    return row;
  });
}

export const payrollPage: Page = crudPage({
  title: 'เงินเดือน (Payroll) — สรุปงวด',
  formHtml: `
    <label>ปี<input name="periodYear" type="number" value="2025" required></label>
    <label>เดือน<input name="periodMonth" type="number" min="1" max="12" value="4" required></label>
    <label>ศูนย์ต้นทุน<input name="costCenter" placeholder="สำนักงานใหญ่"></label>
    ${PAYROLL_FIELDS.map(([n, l]) => `<label>${l}<input name="${n}" inputmode="decimal" placeholder="0.00"></label>`).join('')}`,
  onSubmit: async (f) => {
    const body: Row = {
      periodYear: Number(val(f, 'periodYear')),
      periodMonth: Number(val(f, 'periodMonth')),
    };
    const cc = opt(f, 'costCenter');
    if (cc) body['costCenter'] = cc;
    for (const [n] of PAYROLL_FIELDS) body[n] = val(f, n) || '0';
    await api.createPayroll(body);
  },
  load: async () => {
    const { items } = await api.listPayroll();
    return dataTable(
      ['งวด', 'ศูนย์ต้นทุน', 'เงินเดือน', 'โบนัส/คอม', 'สมทบนายจ้าง', 'หักพนักงาน', 'จ่ายสุทธิ'],
      items.map((r: Row) => ({
        cells: [
          `${esc(r['period_year'])}-${String(r['period_month']).padStart(2, '0')}`,
          esc(r['cost_center'] ?? '—'),
          money(r['salary']),
          money(Number(r['bonus'] ?? 0) + Number(r['commission'] ?? 0)),
          money(Number(r['employer_sso'] ?? 0) + Number(r['employer_wcf'] ?? 0)),
          money(
            Number(r['employee_sso'] ?? 0) +
              Number(r['employee_tax'] ?? 0) +
              Number(r['employee_loan_gsl'] ?? 0) +
              Number(r['other_deductions'] ?? 0),
          ),
          money(r['net_pay']),
        ],
      })),
    );
  },
  extra: `<section class="card">
    <h3>อัปโหลดสรุปเงินเดือน (CSV)</h3>
    <p class="foot-note">หัวตาราง: <code>periodYear,periodMonth,costCenter,salary,wage,ot,bonus,commission,allowance,welfare,employerSso,employerWcf,employeeSso,employeeTax,employeeLoanGsl,otherDeductions</code> — ระบบตรวจก่อนบันทึกทีละงวด</p>
    <form id="payroll-upload" class="grid-form">
      <label>ไฟล์ CSV<input type="file" name="file" accept=".csv,text/csv" required></label>
      <div class="form-actions"><button type="submit">ตรวจและบันทึก</button><span id="upload-msg" class="muted"></span></div>
    </form>
    <div id="upload-result"></div>
  </section>`,
});

// ผูก upload หลัง crudPage mount (เพิ่ม behavior โดยไม่แก้ helper กลาง)
const basePayrollMount = payrollPage.mount?.bind(payrollPage);
payrollPage.mount = (root: HTMLElement): void => {
  basePayrollMount?.(root);
  if (!getTenant()) return;
  const form = root.querySelector<HTMLFormElement>('#payroll-upload');
  const msg = root.querySelector<HTMLElement>('#upload-msg');
  const result = root.querySelector<HTMLElement>('#upload-result');
  if (!form || !msg || !result) return;
  bindSubmit(form, msg, async (f) => {
    const input = f.elements.namedItem('file') as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) throw new Error('เลือกไฟล์ CSV ก่อน');
    const rows = parsePayrollCsv(await file.text());
    const errors: string[] = [];
    let saved = 0;
    for (const [i, row] of rows.entries()) {
      try {
        await api.createPayroll(row);
        saved += 1;
      } catch (e) {
        errors.push(`แถวที่ ${i + 1}: ${e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'}`);
      }
    }
    result.innerHTML = `<p class="muted">บันทึกสำเร็จ ${saved}/${rows.length} งวด</p>${
      errors.length > 0 ? `<ul class="error">${errors.map((e) => `<li>${esc(e)}</li>`).join('')}</ul>` : ''
    }`;
    await refreshList(root);
  });
};

// ---------------------------------------------------------------------------
// T094 — ทรัพย์สินและค่าเสื่อม (ทะเบียน + อนุมัติ + ตารางค่าเสื่อม + จำหน่าย)
// ---------------------------------------------------------------------------
export const assetsPage: Page = crudPage({
  title: 'ทรัพย์สินและค่าเสื่อม',
  formHtml: `
    <label>ชื่อทรัพย์สิน<input name="name" placeholder="เครื่องพิมพ์" required></label>
    <label>ประเภท<input name="assetType" placeholder="อุปกรณ์สำนักงาน"></label>
    <label>ราคาทุน<input name="cost" placeholder="120000.00" required></label>
    <label>มูลค่าซาก<input name="salvageValue" placeholder="0.00"></label>
    <label>อายุใช้งาน (เดือน)<input name="usefulLifeMonths" type="number" placeholder="60"></label>
    <label>วันที่ซื้อ<input name="purchaseDate" type="date"></label>
    <label>วันที่พร้อมใช้งาน<input name="readyDate" type="date"></label>
    <label>สถานที่<input name="location" placeholder="สำนักงานใหญ่"></label>
    <label>วิธีคิดค่าเสื่อม<select name="method"><option value="straight_line">เส้นตรง</option><option value="declining_balance">ยอดลดลง</option><option value="none">ไม่คิด</option></select></label>
    <label class="check"><input name="approved" type="checkbox"> ยืนยันว่าเป็นทรัพย์สิน (ถ้าไม่ติ๊ก จะเข้าคิวรอตรวจ)</label>`,
  onSubmit: async (f) => {
    const body: Row = { name: val(f, 'name'), cost: val(f, 'cost'), method: val(f, 'method') };
    for (const k of ['assetType', 'salvageValue', 'purchaseDate', 'readyDate', 'location']) {
      const v = opt(f, k);
      if (v) body[k] = v;
    }
    const life = opt(f, 'usefulLifeMonths');
    if (life) body['usefulLifeMonths'] = Number(life);
    if (checked(f, 'approved')) body['approved'] = true;
    await api.createAsset(body);
  },
  load: async () => {
    const { items } = await api.listAssets();
    return dataTable(
      ['ชื่อ', 'ราคาทุน', 'ซาก', 'อายุ(ด.)', 'วิธี', 'สถานะตรวจ', ''],
      items.map((r: Row) => ({
        cls: r['review_status'] === 'needs_review' ? 'row-warn' : undefined,
        cells: [
          esc(r['name']),
          money(r['cost']),
          money(r['salvage_value']),
          esc(r['useful_life_months'] ?? '—'),
          esc(r['method']),
          r['review_status'] === 'ok'
            ? pill('อนุมัติแล้ว', 'ok')
            : pill('รอตรวจ (needs review)', 'warn'),
          `${r['review_status'] === 'ok' ? '' : `<button class="mini" data-approve="${esc(r['id'])}">อนุมัติ</button>`}
           <button class="mini" data-sched="${esc(r['id'])}">ตารางค่าเสื่อม</button>
           <button class="mini" data-evidence="${esc(r['id'])}" data-name="${esc(r['name'])}">แนบหลักฐาน</button>
           <button class="mini" data-dispose="${esc(r['id'])}">จำหน่าย</button>`,
        ],
      })),
    );
  },
  extra: `<section class="card"><h3>รายการจำหน่ายทรัพย์สิน</h3><div id="disposals">กำลังโหลด…</div></section>`,
});

const baseAssetsMount = assetsPage.mount?.bind(assetsPage);
assetsPage.mount = (root: HTMLElement): void => {
  baseAssetsMount?.(root);
  if (!getTenant()) return;
  const listEl = root.querySelector<HTMLElement>('#list')!;
  const disposalsEl = root.querySelector<HTMLElement>('#disposals')!;

  const loadDisposals = async (): Promise<void> => {
    try {
      const [{ items }, assets] = await Promise.all([api.listDisposals(), api.listAssets()]);
      const names = new Map(assets.items.map((a: Row) => [String(a['id']), String(a['name'])]));
      disposalsEl.innerHTML = dataTable(
        ['วันที่', 'ทรัพย์สิน', 'เงินที่ได้รับ', 'มูลค่าตามบัญชี', 'กำไร/ขาดทุน'],
        items.map((r: Row) => ({
          cells: [
            esc(r['disposal_date'] ?? '—'),
            esc(names.get(String(r['asset_id'])) ?? r['asset_id']),
            money(r['proceeds']),
            money(r['book_value_at_disposal']),
            `<span class="num ${Number(r['gain_loss'] ?? 0) < 0 ? 'neg-num' : ''}">${money(r['gain_loss'])}</span>`,
          ],
        })),
        'ยังไม่มีการจำหน่ายทรัพย์สิน',
      );
    } catch (e) {
      disposalsEl.innerHTML = errorBox(e);
    }
  };

  // ผูกปุ่มในตารางใหม่ทุกครั้งที่ list ถูก render (ใช้ event delegation)
  listEl.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('button[data-approve],button[data-sched],button[data-dispose],button[data-evidence]');
    if (!btn) return;
    const approveId = btn.dataset['approve'];
    const schedId = btn.dataset['sched'];
    const disposeId = btn.dataset['dispose'];
    const evidenceId = btn.dataset['evidence'];
    if (evidenceId) {
      const drawer = showDrawer(
        root,
        `หลักฐานทรัพย์สิน — ${btn.dataset['name'] ?? ''}`,
        '<p class="muted">แนบใบเสร็จ/ใบกำกับ/รูปทรัพย์สิน แล้วดาวน์โหลดกลับได้</p>',
      );
      mountAttachmentsPanel(drawer, {
        title: 'เอกสาร/หลักฐาน',
        downloadTarget: 'document',
        list: () => api.listAssetEvidence(evidenceId),
        upload: (file) => api.uploadAssetEvidence(evidenceId, file),
      });
      return;
    }
    if (approveId) {
      void (async () => {
        const reason = await promptDialog({
          title: 'อนุมัติเป็นทรัพย์สิน',
          label: 'เหตุผลการอนุมัติ',
          confirmText: 'อนุมัติ',
        });
        if (!reason) return;
        api
          .approveAsset(approveId, reason)
          .then(() => refreshList(root))
          .catch((e: unknown) => alert(e instanceof Error ? e.message : 'อนุมัติไม่สำเร็จ'));
      })();
    } else if (schedId) {
      void (async () => {
        showDrawer(root, 'ตารางค่าเสื่อมราคา', '<p class="muted">กำลังคำนวณ…</p>');
        try {
          await api.genSchedule(schedId);
          const { items } = await api.listSchedule(schedId);
          showDrawer(
            root,
            `ตารางค่าเสื่อมราคา (${items.length} งวด)`,
            dataTable(
              ['งวด', 'ค่าเสื่อมงวดนี้', 'สะสม', 'มูลค่าคงเหลือ'],
              items.map((r: Row) => ({
                cells: [
                  `${esc(r['period_year'])}-${String(r['period_month']).padStart(2, '0')}`,
                  money(r['amount']),
                  money(r['accumulated']),
                  money(r['book_value']),
                ],
              })),
            ) +
              `<p class="foot-note">ค่าเสื่อมจะเข้าบรรทัด “Depreciation &amp; amortization” ใน P&amp;L เฉพาะทรัพย์สินที่อนุมัติแล้ว (review = ok)</p>`,
          );
        } catch (e) {
          showDrawer(root, 'ตารางค่าเสื่อมราคา', errorBox(e));
        }
      })();
    } else if (disposeId) {
      void (async () => {
        const proceeds = await promptDialog({
          title: 'จำหน่ายทรัพย์สิน',
          label: 'เงินที่ได้รับจากการจำหน่าย (บาท)',
          defaultValue: '0',
          inputMode: 'decimal',
          confirmText: 'ถัดไป',
        });
        if (proceeds === null) return;
        const disposalDate = await promptDialog({
          title: 'จำหน่ายทรัพย์สิน',
          label: 'วันที่จำหน่าย (ปปปป-ดด-วว)',
          placeholder: '2026-01-31',
          confirmText: 'บันทึกจำหน่าย',
        });
        if (!disposalDate) return;
        api
          .disposeAsset({ assetId: disposeId, proceeds, disposalDate })
          .then(loadDisposals)
          .catch((e: unknown) => alert(e instanceof Error ? e.message : 'บันทึกการจำหน่ายไม่สำเร็จ'));
      })();
    }
  });
  void loadDisposals();
};

const ADJUSTMENT_LABELS: Record<string, string> = {
  accrual: 'ค้างจ่าย (accrual)',
  prepaid: 'จ่ายล่วงหน้า (prepaid)',
  reclassification: 'จัดประเภทใหม่',
  closing_adjustment: 'ปรับปรุงงวดปิด',
};

// ---------------------------------------------------------------------------
// T095 — ภาษีและรายการปรับปรุง (ทะเบียนภาษี + แบบภาษี + หนังสือรับรอง WHT)
// ---------------------------------------------------------------------------
export const taxPage: Page = crudPage({
  title: 'ภาษีและรายการปรับปรุง',
  formHtml: `
    <label>ประเภท<select name="kind">
      <option value="output_vat">Output VAT (ภาษีขาย)</option><option value="input_vat">Input VAT (ภาษีซื้อ)</option>
      <option value="wht">หัก ณ ที่จ่าย</option><option value="cit">ภาษีเงินได้นิติบุคคล</option>
      <option value="penalty">เบี้ยปรับ</option><option value="surcharge">เงินเพิ่ม</option></select></label>
    <label>เดือนภาษี<input name="taxMonth" type="date"></label>
    <label>คู่ค้า/ผู้ถูกหัก<input name="counterparty" placeholder="ชื่อผู้เสียภาษี"></label>
    <label>เลขผู้เสียภาษี<input name="counterpartyTaxId" placeholder="0000000000000"></label>
    <label>ฐานภาษี<input name="baseAmount" placeholder="10000.00"></label>
    <label>อัตรา (%)<input name="rate" inputmode="decimal" placeholder="7"></label>
    <label>ยอดภาษี<input name="taxAmount" placeholder="700.00" required></label>
    <label class="check"><input name="confirmed" type="checkbox"> ฝ่ายบัญชียืนยันให้กระทบ P&L (ค่าเริ่มต้น: ไม่กระทบ)</label>`,
  onSubmit: async (f) => {
    const body: Row = { kind: val(f, 'kind'), taxAmount: val(f, 'taxAmount') };
    for (const k of ['taxMonth', 'counterparty', 'counterpartyTaxId', 'baseAmount']) {
      const v = opt(f, k);
      if (v) body[k] = v;
    }
    const rate = opt(f, 'rate');
    if (rate) body['rate'] = Number(rate);
    if (checked(f, 'confirmed')) body['confirmed'] = true;
    await api.createTaxLedger(body);
  },
  load: async () => {
    const { items } = await api.listTaxLedger();
    return dataTable(
      ['เดือนภาษี', 'ประเภท', 'คู่ค้า', 'ฐานภาษี', 'ยอดภาษี', 'กระทบ P&L', ''],
      items.map((r: Row) => ({
        cells: [
          esc(r['tax_month'] ?? '—'),
          esc(r['kind']),
          esc(r['counterparty'] ?? '—'),
          money(r['base_amount']),
          money(r['tax_amount']),
          r['pnl_impact'] ? pill('ใช่ (ยืนยันแล้ว)', 'ok') : pill('ไม่ — ทะเบียนภาษี'),
          r['pnl_impact'] ? '' : `<button class="mini" data-confirm="${esc(r['id'])}">ยืนยัน</button>`,
        ],
      })),
    );
  },
  extra: `<section class="card">
      <h3>แบบภาษีที่ยื่น (ภ.พ.30 / ภ.พ.36 / ภ.ง.ด.1,3,53 / ภาษีเงินได้นิติบุคคล)</h3>
      <form id="filing-form" class="grid-form">
        <label>แบบ<select name="formType">
          <option value="pp30">ภ.พ.30</option><option value="pp36">ภ.พ.36</option>
          <option value="pnd1">ภ.ง.ด.1</option><option value="pnd3">ภ.ง.ด.3</option>
          <option value="pnd53">ภ.ง.ด.53</option><option value="cit">ภาษีเงินได้นิติบุคคล</option></select></label>
        <label>ปีภาษี<input name="periodYear" type="number" value="2025" required></label>
        <label>เดือนภาษี<input name="periodMonth" type="number" min="1" max="12" placeholder="4"></label>
        <label>ยอดภาษีที่ยื่น<input name="amount" placeholder="0.00"></label>
        <label>เบี้ยปรับ<input name="penalty" placeholder="0.00"></label>
        <label>เงินเพิ่ม<input name="surcharge" placeholder="0.00"></label>
        <label>วันที่ยื่น<input name="filedDate" type="date"></label>
        <label>วันที่จ่าย<input name="paidDate" type="date"></label>
        <label>เลขรับแบบ<input name="refNo" placeholder="เลขอ้างอิง"></label>
        <div class="form-actions"><button type="submit">บันทึกแบบภาษี</button><span id="filing-msg" class="muted"></span></div>
      </form>
      <div id="filing-list">กำลังโหลด…</div>
    </section>
    <section class="card">
      <h3>หนังสือรับรองหัก ณ ที่จ่าย</h3>
      <form id="wht-form" class="grid-form">
        <label>เลขหนังสือ<input name="certNo" placeholder="WHT-2025-001" required></label>
        <label>ประเภทแบบ<select name="whtType"><option value="pnd3">ภ.ง.ด.3</option><option value="pnd53">ภ.ง.ด.53</option><option value="pnd1">ภ.ง.ด.1</option><option value="other">อื่น ๆ</option></select></label>
        <label>ทิศทาง<select name="direction"><option value="withheld_by_us">กิจการเป็นผู้หัก</option><option value="withheld_from_us">กิจการถูกหัก</option></select></label>
        <label>วันที่<input name="issueDate" type="date"></label>
        <label>ผู้ถูกหัก/ผู้รับเงิน<input name="counterparty"></label>
        <label>เลขผู้เสียภาษี<input name="counterpartyTaxId"></label>
        <label>ฐานภาษี<input name="baseAmount" placeholder="10000.00"></label>
        <label>ยอดภาษีหัก<input name="taxAmount" placeholder="300.00" required></label>
        <div class="form-actions"><button type="submit">บันทึกหนังสือรับรอง</button><span id="wht-msg" class="muted"></span></div>
      </form>
      <div id="wht-list">กำลังโหลด…</div>
    </section>
    <section class="card">
      <h3>รายการปรับปรุงบัญชี (accrual / prepaid / reclassification / reversal)</h3>
      <p class="foot-note">บันทึกเป็นรายการปรับปรุงในฟอร์มกลาง — ใช้แก้งวดที่ปิดแล้วได้โดยไม่ลบประวัติเดิม</p>
      <form id="adj-form" class="grid-form">
        <label>ชนิดการปรับปรุง<select name="adjustmentType">
          <option value="accrual">ค่าใช้จ่ายค้างจ่าย (accrual)</option>
          <option value="prepaid">ค่าใช้จ่ายจ่ายล่วงหน้า (prepaid)</option>
          <option value="reclassification">จัดประเภทใหม่ (reclassification)</option>
          <option value="closing_adjustment">ปรับปรุงงวดปิด</option>
        </select></label>
        <label>วันที่ลงบัญชี<input name="accountingDate" type="date" required></label>
        <label>หมวด P&L<select name="pnlCategory">
          <option value="">— ไม่ระบุ —</option>
          ${PNL_CATEGORY_OPTIONS.map((o) => `<option value="${o.value}">${o.label}</option>`).join('')}
        </select></label>
        <label>รอบบริการเริ่ม<input name="servicePeriodStart" type="date"></label>
        <label>รอบบริการสิ้นสุด<input name="servicePeriodEnd" type="date"></label>
        <label>จำนวนเงิน<input name="totalAmount" inputmode="decimal" placeholder="0.00" required></label>
        <label>เหตุผล (บังคับ)<input name="reason" placeholder="เช่น ตั้งค้างจ่ายค่าเช่าเดือน เม.ย." required></label>
        <div class="form-actions"><button type="submit">บันทึกรายการปรับปรุง</button><span id="adj-msg" class="muted"></span></div>
      </form>
      <div id="adj-list">กำลังโหลด…</div>
    </section>`,
});

const baseTaxMount = taxPage.mount?.bind(taxPage);
taxPage.mount = (root: HTMLElement): void => {
  baseTaxMount?.(root);
  if (!getTenant()) return;
  const listEl = root.querySelector<HTMLElement>('#list')!;
  listEl.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('button[data-confirm]');
    if (!btn) return;
    void (async () => {
      const reason = await promptDialog({
        title: 'ยืนยันให้กระทบ P&L',
        label: 'เหตุผลที่ฝ่ายบัญชียืนยัน',
        confirmText: 'ยืนยัน',
      });
      if (!reason) return;
      api
        .confirmTaxLedger(btn.dataset['confirm']!, reason)
        .then(() => refreshList(root))
        .catch((e: unknown) => alert(e instanceof Error ? e.message : 'ยืนยันไม่สำเร็จ'));
    })();
  });

  const filingList = root.querySelector<HTMLElement>('#filing-list')!;
  const loadFilings = async (): Promise<void> => {
    try {
      const { items } = await api.listTaxFilings();
      filingList.innerHTML = dataTable(
        ['แบบ', 'งวดภาษี', 'ยอดภาษี', 'เบี้ยปรับ/เงินเพิ่ม', 'วันที่ยื่น', 'วันที่จ่าย', 'เลขรับแบบ'],
        items.map((r: Row) => ({
          cells: [
            esc(r['form_type']),
            `${esc(r['period_year'])}${r['period_month'] ? `-${String(r['period_month']).padStart(2, '0')}` : ''}`,
            money(r['amount']),
            money(Number(r['penalty'] ?? 0) + Number(r['surcharge'] ?? 0)),
            esc(r['filed_date'] ?? '—'),
            esc(r['paid_date'] ?? '—'),
            esc(r['ref_no'] ?? '—'),
          ],
        })),
        'ยังไม่มีการยื่นแบบ',
      );
    } catch (e) {
      filingList.innerHTML = errorBox(e);
    }
  };
  const filingForm = root.querySelector<HTMLFormElement>('#filing-form')!;
  bindSubmit(
    filingForm,
    root.querySelector<HTMLElement>('#filing-msg')!,
    async (f) => {
      const body: Row = {
        formType: val(f, 'formType'),
        periodYear: Number(val(f, 'periodYear')),
      };
      const month = opt(f, 'periodMonth');
      if (month) body['periodMonth'] = Number(month);
      for (const k of ['amount', 'penalty', 'surcharge', 'filedDate', 'paidDate', 'refNo']) {
        const v = opt(f, k);
        if (v) body[k] = v;
      }
      await api.createTaxFiling(body);
    },
    loadFilings,
  );

  const whtList = root.querySelector<HTMLElement>('#wht-list')!;
  const loadWht = async (): Promise<void> => {
    try {
      const { items } = await api.listWhtCerts();
      whtList.innerHTML = dataTable(
        ['เลขหนังสือ', 'แบบ', 'ทิศทาง', 'วันที่', 'ผู้ถูกหัก', 'ฐานภาษี', 'ภาษีหัก'],
        items.map((r: Row) => ({
          cells: [
            esc(r['cert_no'] ?? '—'),
            esc(r['wht_type']),
            r['direction'] === 'withheld_from_us' ? 'กิจการถูกหัก' : 'กิจการเป็นผู้หัก',
            esc(r['issue_date'] ?? '—'),
            esc(r['counterparty'] ?? '—'),
            money(r['base_amount']),
            money(r['tax_amount']),
          ],
        })),
        'ยังไม่มีหนังสือรับรอง',
      );
    } catch (e) {
      whtList.innerHTML = errorBox(e);
    }
  };
  const whtForm = root.querySelector<HTMLFormElement>('#wht-form')!;
  bindSubmit(
    whtForm,
    root.querySelector<HTMLElement>('#wht-msg')!,
    async (f) => {
      const body: Row = {
        certNo: val(f, 'certNo'),
        whtType: val(f, 'whtType'),
        direction: val(f, 'direction'),
        taxAmount: val(f, 'taxAmount'),
      };
      for (const k of ['issueDate', 'counterparty', 'counterpartyTaxId', 'baseAmount']) {
        const v = opt(f, k);
        if (v) body[k] = v;
      }
      await api.createWhtCert(body);
    },
    loadWht,
  );

  const adjList = root.querySelector<HTMLElement>('#adj-list')!;
  const loadAdjustments = async (): Promise<void> => {
    try {
      const { items } = await api.listEntries({ classification: 'adjustment', limit: 25 });
      const rev = await api.listEntries({ classification: 'reversal', limit: 25 });
      const all = [...items, ...rev.items];
      adjList.innerHTML = dataTable(
        ['วันที่ลงบัญชี', 'ชนิด', 'หมวด P&L', 'จำนวนเงิน', 'เหตุผล'],
        all.map((r: Row) => ({
          cells: [
            esc(r['accounting_date'] ?? r['event_date'] ?? '—'),
            esc(
              r['classification'] === 'reversal'
                ? 'กลับรายการ'
                : ADJUSTMENT_LABELS[String(r['document_type'] ?? '')] ?? 'รายการปรับปรุง',
            ),
            esc(r['pnl_category'] ?? '—'),
            money(r['total_amount']),
            esc(r['reason'] ?? '—'),
          ],
        })),
        'ยังไม่มีรายการปรับปรุง',
      );
    } catch (e) {
      adjList.innerHTML = errorBox(e);
    }
  };
  const adjForm = root.querySelector<HTMLFormElement>('#adj-form')!;
  bindSubmit(
    adjForm,
    root.querySelector<HTMLElement>('#adj-msg')!,
    async (f) => {
      const body: Row = {
        classification: 'adjustment',
        // เก็บชนิดการปรับปรุงไว้ใน document_type (ฟอร์มกลางใช้ช่องร่วม)
        documentType: val(f, 'adjustmentType'),
        accountingDate: val(f, 'accountingDate'),
        eventDate: val(f, 'accountingDate'),
        totalAmount: val(f, 'totalAmount'),
        reason: val(f, 'reason'),
      };
      for (const k of ['pnlCategory', 'servicePeriodStart', 'servicePeriodEnd']) {
        const v = opt(f, k);
        if (v) body[k] = v;
      }
      await api.createEntry(body);
    },
    loadAdjustments,
  );

  void loadFilings();
  void loadWht();
  void loadAdjustments();
};
